import React, { useState, useEffect, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import collegeConfig from '../../config/collegeConfig';
import {
  FaPlus, FaArrowLeft, FaCalendarAlt, FaEdit, FaTrash,
  FaCheckCircle, FaTimesCircle, FaClipboardList, FaUserGraduate,
  FaCheck, FaTimes, FaExclamationTriangle, FaGraduationCap, FaFilePdf
} from 'react-icons/fa';

const InternalTests = ({ user, assignedCourse, allStudents }) => {
  const [view, setView] = useState('list'); // 'list' | 'create' | 'edit' | 'grade'
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [marks, setMarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [selectedBatch, setSelectedBatch] = useState('');
  const [editingTest, setEditingTest] = useState(null); // { _id, testName, date, maxMarks, batch }
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const validBatches = (() => {
    const minStartYear = new Date().getFullYear() - 4;
    return [...new Set((allStudents || []).map(s => s.batch).filter(Boolean))]
      .filter(b => {
        const startYear = parseInt(b.split('-')[0]);
        return !isNaN(startYear) && startYear >= minStartYear;
      })
      .sort();
  })();

  const [form, setForm] = useState({ testName: '', date: '', maxMarks: 50, batch: '' });

  const token = localStorage.getItem('token');

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3500);
  };

  const fetchTests = useCallback(async () => {
    if (!assignedCourse) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/internal-tests/course/${assignedCourse._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) setTests(data.tests);
    } catch (err) {
      console.error('Fetch tests error:', err);
    }
    setLoading(false);
  }, [assignedCourse, token]);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  const fetchMarks = async (test) => {
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/internal-tests/${test._id}/marks`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        // Build a map of studentId → mark
        const markMap = {};
        data.marks.forEach(m => { markMap[m.studentId._id || m.studentId] = m; });

        // Create rows for ALL students; pre-fill if mark exists
        const rows = allStudents.map(s => {
          const sid = s._id || s.id;
          const existing = markMap[sid];
          return {
            studentId: sid,
            studentName: `${s.profile?.firstName || ''} ${s.profile?.lastName || ''}`.trim(),
            email: s.email,
            marksObtained: existing?.marksObtained ?? '',
            isAbsent: existing?.isAbsent || false,
            isSelfSubmitted: existing?.isSelfSubmitted || false,
            selfMarks: existing?.selfMarks ?? null,
            verificationStatus: existing?.verificationStatus || 'pending',
            gradedBy: existing?.gradedBy || null,
            _markId: existing?._id || null
          };
        });
        setMarks(rows);
      }
    } catch (err) {
      console.error('Fetch marks error:', err);
    }
  };

  const openGrade = async (test) => {
    setSelectedTest(test);
    // Auto-select batch if the test targets a specific one
    if (test.batch) setSelectedBatch(test.batch);
    await fetchMarks(test);
    setView('grade');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.testName || !form.date || !form.maxMarks) return;
    setSaving(true);
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/internal-tests`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          testName: form.testName,
          date: form.date,
          maxMarks: Number(form.maxMarks),
          courseId: assignedCourse._id,
          department: assignedCourse.department || '',
          batch: form.batch
        })
      });
      const data = await res.json();
      if (data.success) {
        showMessage('Test created successfully!');
        setForm({ testName: '', date: '', maxMarks: 50, batch: '' });
        setView('list');
        fetchTests();
      } else {
        showMessage(data.message || 'Failed to create test.', 'error');
      }
    } catch (err) {
      showMessage('Error creating test.', 'error');
    }
    setSaving(false);
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Delete this test? This cannot be undone.')) return;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/internal-tests/${testId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        showMessage('Test deleted.');
        fetchTests();
      }
    } catch (err) {
      showMessage('Error deleting test.', 'error');
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editingTest) return;
    setSaving(true);
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/internal-tests/${editingTest._id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          testName: editingTest.testName,
          date: editingTest.date,
          maxMarks: Number(editingTest.maxMarks),
          batch: editingTest.batch
        })
      });
      const data = await res.json();
      if (data.success) {
        showMessage('Test updated successfully!');
        setEditingTest(null);
        setView('list');
        fetchTests();
      } else {
        showMessage(data.message || 'Failed to update test.', 'error');
      }
    } catch (err) {
      showMessage('Error updating test.', 'error');
    }
    setSaving(false);
  };

  const handleMarkChange = (studentId, field, value) => {
    setMarks(prev => prev.map(m =>
      m.studentId === studentId ? { ...m, [field]: value } : m
    ));
  };

  const saveMark = async (studentId) => {
    const row = marks.find(m => m.studentId === studentId);
    if (!row) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/internal-tests/${selectedTest._id}/marks/${studentId}`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            marksObtained: row.isAbsent ? null : Number(row.marksObtained),
            isAbsent: row.isAbsent,
            courseId: assignedCourse._id
          })
        }
      );
      const data = await res.json();
      if (data.success) {
        showMessage('Marks saved!');
        await fetchMarks(selectedTest);
      } else {
        showMessage(data.message || 'Failed to save.', 'error');
      }
    } catch (err) {
      showMessage('Error saving marks.', 'error');
    }
    setSaving(false);
  };

  const handleVerify = async (studentId, action) => {
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/internal-tests/${selectedTest._id}/marks/${studentId}/verify`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action })
        }
      );
      const data = await res.json();
      if (data.success) {
        showMessage(action === 'verify' ? 'Marks verified!' : 'Marks rejected — student marked absent.');
        await fetchMarks(selectedTest);
      }
    } catch (err) {
      showMessage('Error updating verification.', 'error');
    }
  };

  const saveAllMarks = async () => {
    setSaving(true);
    let errors = 0;
    for (const row of marks) {
      try {
        const res = await fetch(
          `${collegeConfig.api.baseURL}/internal-tests/${selectedTest._id}/marks/${row.studentId}`,
          {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              marksObtained: row.isAbsent ? null : (row.marksObtained !== '' ? Number(row.marksObtained) : null),
              isAbsent: row.isAbsent,
              courseId: assignedCourse._id
            })
          }
        );
        if (!res.ok) errors++;
      } catch { errors++; }
    }
    setSaving(false);
    if (errors === 0) {
      showMessage('All marks saved successfully!');
      await fetchMarks(selectedTest);
    } else {
      showMessage(`Saved with ${errors} error(s).`, 'error');
    }
  };

  // ─── PDF GENERATION ──────────────────────────────────────────────────────────

  const generatePDF = async () => {
    if (!selectedBatch) return;
    setGeneratingPDF(true);
    try {
      // Tests relevant to this batch (batch='' means all batches)
      const relevantTests = tests.filter(t => !t.batch || t.batch === selectedBatch);
      if (relevantTests.length === 0) {
        showMessage('No tests found for this batch.', 'error');
        setGeneratingPDF(false);
        return;
      }

      const batchStudents = (allStudents || []).filter(s => s.batch === selectedBatch);
      if (batchStudents.length === 0) {
        showMessage('No students found for this batch.', 'error');
        setGeneratingPDF(false);
        return;
      }

      // Fetch HOD silently
      let hodName = '';
      try {
        const hodRes = await fetch(
          `${collegeConfig.api.baseURL}/internal-tests/course/${assignedCourse._id}/hod`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).then(r => r.json());
        hodName = hodRes.hod || '';
      } catch (_) {}

      // Fetch marks for all tests in parallel
      const allMarksData = await Promise.all(
        relevantTests.map(t =>
          fetch(`${collegeConfig.api.baseURL}/internal-tests/${t._id}/marks`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(r => r.json())
        )
      );

      // Build mark matrix: testId → studentId → mark
      const matrix = {};
      allMarksData.forEach((data, i) => {
        const tid = String(relevantTests[i]._id);
        matrix[tid] = {};
        (data.marks || []).forEach(m => {
          const sid = String(m.studentId?._id || m.studentId);
          matrix[tid][sid] = m;
        });
      });

      // ── Table: students as rows, tests as columns ──────────────────────────
      const totalMaxMarks = relevantTests.reduce((sum, t) => sum + Number(t.maxMarks), 0);

      // Header: Student Name | Test1 (date, /max) | Test2 ... | Total
      const testHeaders = relevantTests.map(t =>
        `${t.testName}\n${new Date(t.date).toLocaleDateString('en-IN')}\n(/${t.maxMarks})`
      );
      const head = [['Student Name', ...testHeaders, `Total\n(/${totalMaxMarks})`, '%']];

      // One row per student
      const body = batchStudents.map((s, idx) => {
        const sid = String(s._id || s.id);
        let totalObtained = 0;
        const testCells = relevantTests.map(test => {
          const mark = matrix[String(test._id)]?.[sid];
          if (!mark) return '-';
          if (mark.isAbsent) return 'AB';
          if (mark.marksObtained !== null && mark.marksObtained !== undefined) {
            totalObtained += Number(mark.marksObtained);
            return String(mark.marksObtained);
          }
          return '-';
        });
        const name = `${s.profile?.firstName || ''} ${s.profile?.lastName || ''}`.trim() || 'Unknown';
        const pct = totalMaxMarks > 0 ? ((totalObtained / totalMaxMarks) * 100).toFixed(1) + '%' : '-';
        return [`${idx + 1}. ${name}`, ...testCells, String(totalObtained), pct];
      });

      // ── Build PDF ──────────────────────────────────────────────────────────
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 14;

      // Logo
      try { doc.addImage(collegeConfig.logo, 'PNG', margin, 7, 22, 22); } catch (_) {}

      // College name
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text(collegeConfig.collegeName, pageWidth / 2, 16, { align: 'center' });

      // Sub-header
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      const courseInfo = [
        assignedCourse?.name && `Course: ${assignedCourse.name}`,
        assignedCourse?.level && `Level: ${assignedCourse.level}`,
        assignedCourse?.code && `Code: ${assignedCourse.code}`,
        `Batch: ${selectedBatch}`,
      ].filter(Boolean).join('   |   ');
      doc.text(courseInfo, pageWidth / 2, 24, { align: 'center' });
      doc.text(
        `Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}`,
        pageWidth / 2, 30, { align: 'center' }
      );

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Internal Test Marks Report', pageWidth / 2, 38, { align: 'center' });

      doc.setDrawColor(52, 152, 219);
      doc.setLineWidth(0.5);
      doc.line(margin, 41, pageWidth - margin, 41);

      // Column widths: Student Name | tests... | Total | %
      const nameColW = 50;
      const totalColW = 22;
      const pctColW = 18;
      const testColW = Math.max(16, Math.min(35, (pageWidth - margin * 2 - nameColW - totalColW - pctColW) / relevantTests.length));
      const totalColIndex = relevantTests.length + 1;
      const pctColIndex = relevantTests.length + 2;

      const columnStyles = {
        0: { cellWidth: nameColW },
        [totalColIndex]: { cellWidth: totalColW, halign: 'center', fontStyle: 'bold' },
        [pctColIndex]: { cellWidth: pctColW, halign: 'center', fontStyle: 'bold' },
      };
      relevantTests.forEach((_, i) => {
        columnStyles[i + 1] = { cellWidth: testColW, halign: 'center' };
      });

      autoTable(doc, {
        head,
        body,
        startY: 44,
        styles: { fontSize: 7.5, cellPadding: 2, overflow: 'linebreak' },
        headStyles: {
          fillColor: [52, 152, 219],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 7.5,
          halign: 'center',
          valign: 'middle',
          minCellHeight: 14
        },
        columnStyles,
        didParseCell: (data) => {
          if (data.section === 'body' && (data.column.index === totalColIndex || data.column.index === pctColIndex)) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [235, 245, 255];
          }
          if (data.section === 'body' && data.cell.text[0] === 'AB') {
            data.cell.styles.textColor = [192, 57, 43];
            data.cell.styles.fontStyle = 'bold';
          }
        },
        margin: { left: margin, right: margin }
      });

      // HOD signature (bottom-right)
      const tableEndY = (doc.lastAutoTable?.finalY || 170) + 14;
      const sigX = pageWidth - margin - 80;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`HOD: ${hodName}`, sigX, tableEndY);
      doc.setDrawColor(80, 80, 80);
      doc.setLineWidth(0.4);
      doc.line(sigX, tableEndY + 12, pageWidth - margin, tableEndY + 12);
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text('Signature', sigX, tableEndY + 17);
      doc.setTextColor(0, 0, 0);

      const fileName = `InternalTest_${selectedBatch}_${new Date().toISOString().split('T')[0]}.pdf`;

      // ── Save: native Capacitor vs web browser ─────────────────────────────
      if (Capacitor.isNativePlatform()) {
        const base64 = doc.output('datauristring').split(',')[1];
        const writeResult = await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Documents,
          recursive: true
        });
        await FileOpener.open({
          filePath: writeResult.uri,
          contentType: 'application/pdf'
        });
      } else {
        doc.save(fileName);
      }

      showMessage('PDF downloaded successfully!');
    } catch (err) {
      console.error('PDF generation error:', err);
      showMessage('Failed to generate PDF.', 'error');
    }
    setGeneratingPDF(false);
  };

  // ─── RENDER ─────────────────────────────────────────────────────────────────

  const renderMessage = () => message.text && (
    <div style={{
      padding: '12px 18px', borderRadius: '8px', marginBottom: '16px', fontWeight: 600,
      background: message.type === 'error' ? '#fdecea' : '#eafaf1',
      color: message.type === 'error' ? '#c0392b' : '#1e8449',
      border: `1px solid ${message.type === 'error' ? '#f5b7b1' : '#a9dfbf'}`
    }}>{message.text}</div>
  );

  if (view === 'create') {
    return (
      <div style={{ maxWidth: 540, margin: '0 auto' }}>
        <button onClick={() => setView('list')} style={styles.backBtn}>
          <FaArrowLeft /> Back to Tests
        </button>
        <h2 style={styles.heading}><FaPlus style={{ marginRight: 8 }} />Create Internal Test</h2>
        {renderMessage()}
        <form onSubmit={handleCreate} style={styles.card}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Test / Subject Name *</label>
            <input
              style={styles.input}
              value={form.testName}
              onChange={e => setForm(f => ({ ...f, testName: e.target.value }))}
              placeholder="e.g. GIT, Upper GIT, Breast..."
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Date *</label>
            <input
              style={styles.input}
              type="date"
              value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Maximum Marks *</label>
            <input
              style={styles.input}
              type="number"
              min={1}
              value={form.maxMarks}
              onChange={e => setForm(f => ({ ...f, maxMarks: e.target.value }))}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}><FaGraduationCap style={{ marginRight: 6 }} />Batch</label>
            <select
              style={styles.input}
              value={form.batch}
              onChange={e => setForm(f => ({ ...f, batch: e.target.value }))}
            >
              <option value="">All Batches</option>
              {validBatches.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <button type="submit" disabled={saving} style={styles.primaryBtn}>
            {saving ? 'Creating...' : 'Create Test'}
          </button>
        </form>
      </div>
    );
  }

  if (view === 'edit' && editingTest) {
    return (
      <div style={{ maxWidth: 540, margin: '0 auto' }}>
        <button onClick={() => { setView('list'); setEditingTest(null); }} style={styles.backBtn}>
          <FaArrowLeft /> Back to Tests
        </button>
        <h2 style={styles.heading}><FaEdit style={{ marginRight: 8 }} />Edit Internal Test</h2>
        {renderMessage()}
        <form onSubmit={handleEditSave} style={styles.card}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Test / Subject Name *</label>
            <input
              style={styles.input}
              value={editingTest.testName}
              onChange={e => setEditingTest(t => ({ ...t, testName: e.target.value }))}
              placeholder="e.g. GIT, Upper GIT, Breast..."
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Date *</label>
            <input
              style={styles.input}
              type="date"
              value={editingTest.date ? editingTest.date.split('T')[0] : ''}
              onChange={e => setEditingTest(t => ({ ...t, date: e.target.value }))}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Maximum Marks *</label>
            <input
              style={styles.input}
              type="number"
              min={1}
              value={editingTest.maxMarks}
              onChange={e => setEditingTest(t => ({ ...t, maxMarks: e.target.value }))}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}><FaGraduationCap style={{ marginRight: 6 }} />Batch</label>
            <select
              style={styles.input}
              value={editingTest.batch || ''}
              onChange={e => setEditingTest(t => ({ ...t, batch: e.target.value }))}
            >
              <option value="">All Batches</option>
              {validBatches.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" disabled={saving} style={styles.primaryBtn}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => { setView('list'); setEditingTest(null); }}
              style={{ ...styles.primaryBtn, background: '#95A5A6' }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (view === 'grade' && selectedTest) {
    const batchMarks = selectedBatch
      ? marks.filter(row => {
          const stu = allStudents.find(s => String(s._id || s.id) === String(row.studentId));
          return stu?.batch === selectedBatch;
        })
      : [];

    const pendingSelfSubmissions = batchMarks.filter(
      m => m.isSelfSubmitted && m.verificationStatus === 'pending' && !m.gradedBy
    );

    return (
      <div>
        <button onClick={() => setView('list')} style={styles.backBtn}>
          <FaArrowLeft /> Back to Tests
        </button>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h2 style={{ ...styles.heading, marginBottom: 4 }}>
              <FaClipboardList style={{ marginRight: 8, color: '#3498DB' }} />
              {selectedTest.testName}
            </h2>
            <p style={{ color: '#666', margin: 0 }}>
              Date: {new Date(selectedTest.date).toLocaleDateString()} &nbsp;|&nbsp; Max Marks: {selectedTest.maxMarks}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {selectedTest.batch ? (
              <span style={{ ...styles.batchBadge, fontSize: 13, padding: '5px 14px' }}>
                <FaGraduationCap style={{ marginRight: 5 }} />{selectedTest.batch}
              </span>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontWeight: 600, color: '#444' }}>Batch:</label>
                <select
                  value={selectedBatch}
                  onChange={(e) => setSelectedBatch(e.target.value)}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
                >
                  <option value="">Select Batch</option>
                  {validBatches.filter(b => allStudents.some(s => s.batch === b)).map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
            )}
            <button onClick={saveAllMarks} disabled={saving} style={styles.primaryBtn}>
              {saving ? 'Saving...' : 'Save All Marks'}
            </button>
          </div>
        </div>

        {renderMessage()}

        {!selectedBatch && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888', background: '#f8f9fa', borderRadius: 10, marginBottom: 16 }}>
            Please select a batch to view and grade students.
          </div>
        )}

        {pendingSelfSubmissions.length > 0 && (
          <div style={{ background: '#FFF9E6', border: '1px solid #F39C12', borderRadius: 10, padding: '14px 18px', marginBottom: 18 }}>
            <p style={{ margin: 0, fontWeight: 600, color: '#E67E22' }}>
              <FaExclamationTriangle style={{ marginRight: 6 }} />
              {pendingSelfSubmissions.length} student(s) have self-submitted marks pending your verification.
            </p>
          </div>
        )}

        {selectedBatch && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 14 }}>
            {batchMarks.map((row, idx) => {
              const hasPendingSelf = row.isSelfSubmitted && row.verificationStatus === 'pending' && !row.gradedBy;
              return (
                <div key={row.studentId} style={{
                  background: 'white', borderRadius: 14, padding: '16px 18px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  border: hasPendingSelf ? '1px solid #F39C12' : '1px solid #eee',
                  display: 'flex', flexDirection: 'column', gap: 12
                }}>
                  {/* Student header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', background: '#EBF5FB',
                        color: '#2980b9', fontWeight: 700, fontSize: 13,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                      }}>{idx + 1}</div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#2C3E50', lineHeight: 1.3 }}>
                          {row.studentName || 'Unknown'}
                        </div>
                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{row.email}</div>
                      </div>
                    </div>
                    {/* Absent toggle */}
                    <button
                      onClick={() => handleMarkChange(row.studentId, 'isAbsent', !row.isAbsent)}
                      style={{
                        padding: '5px 12px', borderRadius: 20, cursor: 'pointer',
                        fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0,
                        background: row.isAbsent ? '#fdecea' : '#f5f5f5',
                        color: row.isAbsent ? '#c0392b' : '#777',
                        border: row.isAbsent ? '1px solid #f5b7b1' : '1px solid #ddd'
                      }}
                    >
                      {row.isAbsent ? <><FaTimesCircle style={{ marginRight: 4 }} />Absent</> : 'Mark Absent'}
                    </button>
                  </div>

                  {/* Self-submitted section */}
                  {row.isSelfSubmitted && (
                    <div style={{
                      background: hasPendingSelf ? '#fffbea' : '#f8f9fa',
                      border: `1px solid ${hasPendingSelf ? '#F39C12' : '#ddd'}`,
                      borderRadius: 8, padding: '8px 12px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: hasPendingSelf ? 8 : 0 }}>
                        <span style={{ fontSize: 12, color: '#666' }}>Self-submitted:</span>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>{row.selfMarks}</span>
                        <span style={{
                          padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                          background: row.verificationStatus === 'verified' ? '#eafaf1' : row.verificationStatus === 'rejected' ? '#fdecea' : '#fff3cd',
                          color: row.verificationStatus === 'verified' ? '#1e8449' : row.verificationStatus === 'rejected' ? '#c0392b' : '#856404'
                        }}>
                          {row.verificationStatus}
                        </span>
                      </div>
                      {hasPendingSelf && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => handleVerify(row.studentId, 'verify')}
                            style={{ ...styles.smallBtn, background: '#2ECC71', color: 'white', flex: 1, justifyContent: 'center' }}
                          >
                            <FaCheck style={{ marginRight: 4 }} /> Verify
                          </button>
                          <button
                            onClick={() => handleVerify(row.studentId, 'reject')}
                            style={{ ...styles.smallBtn, background: '#E74C3C', color: 'white', flex: 1, justifyContent: 'center' }}
                          >
                            <FaTimes style={{ marginRight: 4 }} /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Marks input + Save */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {row.isAbsent ? (
                      <span style={{ color: '#E74C3C', fontWeight: 700, flex: 1, fontSize: 14 }}>ABSENT</span>
                    ) : (
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                          type="number"
                          min={0}
                          max={selectedTest.maxMarks}
                          value={row.marksObtained}
                          onChange={e => handleMarkChange(row.studentId, 'marksObtained', e.target.value)}
                          style={{
                            flex: 1, padding: '10px 12px', border: '1px solid #ddd',
                            borderRadius: 8, fontSize: 16, outline: 'none',
                            textAlign: 'center', fontWeight: 700
                          }}
                          placeholder="—"
                        />
                        <span style={{ color: '#888', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                          / {selectedTest.maxMarks}
                        </span>
                      </div>
                    )}
                    <button
                      onClick={() => saveMark(row.studentId)}
                      disabled={saving}
                      style={{ ...styles.primaryBtn, padding: '10px 16px', whiteSpace: 'nowrap' }}
                    >
                      <FaCheckCircle style={{ marginRight: 5 }} /> Save
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ─── LIST VIEW ───────────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={styles.heading}>
          <FaClipboardList style={{ marginRight: 8, color: '#3498DB' }} />
          Internal Tests
        </h2>
        <button onClick={() => setView('create')} style={styles.primaryBtn}>
          <FaPlus style={{ marginRight: 6 }} /> Create Test
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontWeight: 600, color: '#444' }}>Batch:</label>
          <select
            value={selectedBatch}
            onChange={(e) => setSelectedBatch(e.target.value)}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
          >
            <option value="">Select Batch</option>
            {validBatches.filter(b => allStudents.some(s => s.batch === b)).map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>
        {selectedBatch && (
          <button
            onClick={generatePDF}
            disabled={generatingPDF}
            style={{ ...styles.primaryBtn, background: '#E74C3C', padding: '8px 18px' }}
          >
            <FaFilePdf style={{ marginRight: 6 }} />
            {generatingPDF ? 'Generating...' : 'Download PDF'}
          </button>
        )}
      </div>

      {!selectedBatch && tests.length > 0 && (
        <p style={{ margin: '-8px 0 12px', fontSize: 13, color: '#999', display: 'flex', alignItems: 'center', gap: 6 }}>
          <FaFilePdf style={{ color: '#E74C3C', flexShrink: 0 }} />
          To download a PDF of batch results, select a batch above.
        </p>
      )}

      {renderMessage()}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Loading tests...</div>
      ) : tests.length === 0 ? (
        <div style={styles.emptyState}>
          <FaClipboardList size={56} color="#BDC3C7" />
          <h3 style={{ marginTop: 16, color: '#7F8C8D' }}>No Internal Tests Yet</h3>
          <p style={{ color: '#95A5A6' }}>Create your first internal test to get started.</p>
          <button onClick={() => setView('create')} style={{ ...styles.primaryBtn, marginTop: 12 }}>
            <FaPlus style={{ marginRight: 6 }} /> Create Test
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {tests.map(test => (
            <div key={test._id} style={{
              background: 'white', borderRadius: 12, padding: '16px 18px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
              display: 'flex', flexDirection: 'column', gap: 10
            }}>
              {/* Title + batch badge */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, color: '#2C3E50', flex: 1, fontSize: 16, lineHeight: 1.3 }}>{test.testName}</h3>
                {test.batch ? (
                  <span style={styles.batchBadge}>
                    <FaGraduationCap style={{ marginRight: 4 }} />{test.batch}
                  </span>
                ) : (
                  <span style={{ ...styles.batchBadge, background: '#e8f4fd', color: '#2980b9', borderColor: '#b3d7f0' }}>
                    All Batches
                  </span>
                )}
              </div>
              {/* Meta info */}
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13, color: '#666' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FaCalendarAlt />{new Date(test.date).toLocaleDateString()}
                </span>
                <span>Max: <strong>{test.maxMarks}</strong></span>
                <span style={{ color: '#aaa' }}>
                  By: {test.createdBy?.profile?.firstName} {test.createdBy?.profile?.lastName}
                </span>
              </div>
              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 2 }}>
                <button onClick={() => openGrade(test)} style={{ ...styles.primaryBtn, padding: '8px 16px', flex: 1 }}>
                  <FaUserGraduate style={{ marginRight: 5 }} /> Grade Students
                </button>
                <button
                  onClick={() => { setEditingTest({ ...test, date: test.date ? test.date.split('T')[0] : '' }); setView('edit'); }}
                  style={{ ...styles.smallBtn, background: '#eaf4ff', color: '#2980b9', border: '1px solid #b3d7f0', padding: '8px 14px' }}
                  title="Edit test"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDeleteTest(test._id)}
                  style={{ ...styles.smallBtn, background: '#fdecea', color: '#c0392b', border: '1px solid #f5b7b1', padding: '8px 14px' }}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  heading: { fontSize: 22, fontWeight: 700, color: '#2C3E50', margin: '0 0 16px' },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 20,
    background: 'none', border: 'none', color: '#3498DB', fontSize: 15,
    cursor: 'pointer', fontWeight: 600, padding: 0
  },
  card: { background: 'white', borderRadius: 12, padding: 28, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  formGroup: { marginBottom: 18 },
  label: { display: 'block', fontWeight: 600, color: '#2C3E50', marginBottom: 6, fontSize: 14 },
  input: {
    width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 8,
    fontSize: 14, outline: 'none', boxSizing: 'border-box'
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: '#3498DB', color: 'white', border: 'none', borderRadius: 8,
    padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer'
  },
  smallBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12,
    fontWeight: 600, cursor: 'pointer'
  },
  testCard: {
    background: 'white', borderRadius: 12, padding: '18px 22px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.07)', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14
  },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: '#7F8C8D' },
  batchBadge: {
    display: 'inline-flex', alignItems: 'center', fontSize: 12, fontWeight: 600,
    padding: '3px 10px', borderRadius: 20,
    background: '#ede9ff', color: '#5b4fcf', border: '1px solid #c4b5fd'
  }
};

export default InternalTests;
